// Página SupplierDetail - Detalle completo de un proveedor
// Muestra información del proveedor, productos, pedidos de compra y estadísticas

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { getSupplier, getSupplierProducts, getSupplierPurchaseOrders, getSupplierStats } from '../../services/suppliers.service'
import { formatCurrency, formatDate } from '../../utils/helpers'
import Button from '../../components/Button'
import './SupplierDetail.css'

/**
 * Página SupplierDetail
 * Muestra el detalle completo de un proveedor
 */
function SupplierDetail() {
  const { email } = useParams()
  const navigate = useNavigate()
  const decodedEmail = decodeURIComponent(email)
  
  const [supplier, setSupplier] = useState(null)
  const [products, setProducts] = useState([])
  const [purchaseOrders, setPurchaseOrders] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('info') // 'info', 'products', 'orders', 'stats'

  const fetchSupplierData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Obtener información del proveedor, productos, pedidos y estadísticas en paralelo
      const [supplierData, productsData, ordersData, statsData] = await Promise.all([
        getSupplier(decodedEmail),
        getSupplierProducts(decodedEmail),
        getSupplierPurchaseOrders(decodedEmail),
        getSupplierStats(decodedEmail)
      ])
      
      if (!supplierData) {
        setError('Proveedor no encontrado')
        return
      }
      
      setSupplier(supplierData)
      setProducts(productsData)
      setPurchaseOrders(ordersData)
      setStats(statsData)
    } catch (err) {
      console.error('Error cargando proveedor:', err)
      setError('Error al cargar la información del proveedor')
    } finally {
      setLoading(false)
    }
  }, [decodedEmail])

  useEffect(() => {
    fetchSupplierData()
  }, [fetchSupplierData])

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      console.log('Copiado al portapapeles:', text)
    })
  }

  const getOrderStatusLabel = (status) => {
    const labels = {
      pending: 'Pendiente',
      confirmed: 'Confirmado',
      received: 'Recibido',
      cancelled: 'Cancelado'
    }
    return labels[status] || status
  }

  const getOrderStatusClass = (status) => {
    return `status-badge status-${status}`
  }

  if (loading) {
    return (
      <div className="supplier-detail-page">
        <div className="loading-state">
          <p>Cargando información del proveedor...</p>
        </div>
      </div>
    )
  }

  if (error || !supplier) {
    return (
      <div className="supplier-detail-page">
        <div className="error-state">
          <h2>Error</h2>
          <p>{error || 'No se pudo cargar la información del proveedor'}</p>
          <Button onClick={() => navigate('/admin/proveedores')}>
            Volver a Proveedores
          </Button>
        </div>
      </div>
    )
  }

  const primaryAddress = supplier.addresses && supplier.addresses.length > 0 && supplier.primaryAddressIndex !== null
    ? supplier.addresses[supplier.primaryAddressIndex]
    : supplier.addresses?.[0] || null

  return (
    <div className="supplier-detail-page">
      {/* Header */}
      <div className="detail-header">
        <div>
          <button
            className="back-button"
            onClick={() => navigate('/admin/proveedores')}
          >
            ← Volver
          </button>
          <div className="supplier-title-section">
            <h1>{supplier.name}</h1>
            {supplier.businessName && (
              <p className="business-name">{supplier.businessName}</p>
            )}
            <div className="supplier-status">
              <span className={`status-badge ${supplier.active !== false ? 'active' : 'inactive'}`}>
                {supplier.active !== false ? 'Activo' : 'Inactivo'}
              </span>
              {supplier.rating && (
                <span className="rating-badge">
                  ⭐ {supplier.rating}/5
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="header-actions">
          <Link to={`/admin/proveedores/${encodeURIComponent(supplier.email)}/editar`}>
            <Button variant="primary">
              ✏️ Editar Proveedor
            </Button>
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="detail-tabs">
        <button
          className={`tab-button ${activeTab === 'info' ? 'active' : ''}`}
          onClick={() => setActiveTab('info')}
        >
          📋 Información
        </button>
        <button
          className={`tab-button ${activeTab === 'products' ? 'active' : ''}`}
          onClick={() => setActiveTab('products')}
        >
          📦 Productos ({products.length})
        </button>
        <button
          className={`tab-button ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          🛒 Pedidos de Compra ({purchaseOrders.length})
        </button>
        <button
          className={`tab-button ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          📊 Estadísticas
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Tab: Información */}
        {activeTab === 'info' && (
          <div className="info-tab">
            <div className="info-grid">
              {/* Información Básica */}
              <div className="info-card">
                <h3>Información Básica</h3>
                <div className="info-list">
                  <div className="info-item">
                    <span className="info-label">Nombre:</span>
                    <span className="info-value">{supplier.name}</span>
                  </div>
                  {supplier.businessName && (
                    <div className="info-item">
                      <span className="info-label">Razón Social:</span>
                      <span className="info-value">{supplier.businessName}</span>
                    </div>
                  )}
                  {supplier.taxId && (
                    <div className="info-item">
                      <span className="info-label">CUIT/CUIL:</span>
                      <span className="info-value">{supplier.taxId}</span>
                    </div>
                  )}
                  <div className="info-item">
                    <span className="info-label">Email:</span>
                    <span className="info-value">
                      {supplier.email}
                      <button
                        className="copy-btn"
                        onClick={() => copyToClipboard(supplier.email)}
                        title="Copiar"
                      >
                        📋
                      </button>
                    </span>
                  </div>
                </div>
              </div>

              {/* Contacto */}
              <div className="info-card">
                <h3>Contacto</h3>
                <div className="info-list">
                  {supplier.phone && (
                    <div className="info-item">
                      <span className="info-label">Teléfono:</span>
                      <span className="info-value">
                        {supplier.phone}
                        <button
                          className="copy-btn"
                          onClick={() => copyToClipboard(supplier.phone)}
                          title="Copiar"
                        >
                          📋
                        </button>
                      </span>
                    </div>
                  )}
                  {supplier.phoneAlternate && (
                    <div className="info-item">
                      <span className="info-label">Teléfono Alternativo:</span>
                      <span className="info-value">
                        {supplier.phoneAlternate}
                        <button
                          className="copy-btn"
                          onClick={() => copyToClipboard(supplier.phoneAlternate)}
                          title="Copiar"
                        >
                          📋
                        </button>
                      </span>
                    </div>
                  )}
                  {primaryAddress && (
                    <div className="info-item">
                      <span className="info-label">Dirección Principal:</span>
                      <span className="info-value">
                        {primaryAddress.street}, {primaryAddress.city}, {primaryAddress.province}
                        {primaryAddress.postalCode && ` (CP: ${primaryAddress.postalCode})`}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Información Comercial */}
              <div className="info-card">
                <h3>Información Comercial</h3>
                <div className="info-list">
                  {supplier.paymentTerms && (
                    <div className="info-item">
                      <span className="info-label">Condiciones de Pago:</span>
                      <span className="info-value">{supplier.paymentTerms}</span>
                    </div>
                  )}
                  {supplier.deliveryTime && (
                    <div className="info-item">
                      <span className="info-label">Tiempo de Entrega:</span>
                      <span className="info-value">{supplier.deliveryTime}</span>
                    </div>
                  )}
                  {supplier.minimumOrder && (
                    <div className="info-item">
                      <span className="info-label">Pedido Mínimo:</span>
                      <span className="info-value">{formatCurrency(supplier.minimumOrder)}</span>
                    </div>
                  )}
                  {supplier.rating && (
                    <div className="info-item">
                      <span className="info-label">Calificación:</span>
                      <span className="info-value">⭐ {supplier.rating}/5</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Direcciones */}
              {supplier.addresses && supplier.addresses.length > 0 && (
                <div className="info-card full-width">
                  <h3>Direcciones ({supplier.addresses.length})</h3>
                  <div className="addresses-list">
                    {supplier.addresses.map((address, index) => (
                      <div key={index} className="address-item">
                        <div className="address-header">
                          <strong>
                            {address.type === 'fiscal' ? '📋 Fiscal' : 
                             address.type === 'entrega' ? '🚚 Entrega' : '📍 Otro'}
                          </strong>
                          {index === supplier.primaryAddressIndex && (
                            <span className="primary-badge">Principal</span>
                          )}
                        </div>
                        <p>{address.street}, {address.city}, {address.province}</p>
                        {address.postalCode && <p>CP: {address.postalCode}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notas */}
              {supplier.notes && (
                <div className="info-card full-width">
                  <h3>Notas / Observaciones</h3>
                  <p className="notes-text">{supplier.notes}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab: Productos */}
        {activeTab === 'products' && (
          <div className="products-tab">
            {products.length === 0 ? (
              <div className="empty-state">
                <p>Este proveedor no tiene productos asociados</p>
                <Link to="/admin/productos">
                  <Button variant="primary" style={{ marginTop: '1rem' }}>
                    Ver Productos
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <div className="products-info">
                  <p className="products-count">
                    <strong>{products.length}</strong> producto{products.length !== 1 ? 's' : ''} asociado{products.length !== 1 ? 's' : ''} a este proveedor
                  </p>
                  <p className="products-hint">
                    💡 Un producto puede tener múltiples proveedores. Estos son los productos que incluyen a este proveedor en su lista.
                  </p>
                </div>
                <div className="products-grid">
                  {products.map((product) => {
                    // Verificar si este proveedor es el único o uno de varios
                    const supplierIds = product.supplierIds || (product.supplierId ? [product.supplierId] : [])
                    const isMultipleSuppliers = supplierIds.length > 1
                    
                    return (
                      <Link
                        key={product.id}
                        to={`/admin/productos/${product.id}`}
                        className="product-card-link"
                      >
                        <div className="product-card">
                          <h4>{product.name}</h4>
                          <p className="product-sku">SKU: {product.sku || 'N/A'}</p>
                          {product.price && (
                            <p className="product-price">{formatCurrency(product.price)}</p>
                          )}
                          {isMultipleSuppliers && (
                            <p className="product-suppliers-info">
                              📦 {supplierIds.length} proveedor{supplierIds.length !== 1 ? 'es' : ''}
                            </p>
                          )}
                        </div>
                      </Link>
                    )
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* Tab: Pedidos de Compra */}
        {activeTab === 'orders' && (
          <div className="orders-tab">
            {purchaseOrders.length === 0 ? (
              <div className="empty-state">
                <p>No hay pedidos de compra registrados para este proveedor</p>
                <Link to="/admin/pedidos-compra">
                  <Button variant="primary" style={{ marginTop: '1rem' }}>
                    Crear Pedido de Compra
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="orders-table-container">
                <table className="orders-table">
                  <thead>
                    <tr>
                      <th>Número</th>
                      <th>Fecha</th>
                      <th>Estado</th>
                      <th>Total</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchaseOrders.map((order) => (
                      <tr key={order.id}>
                        <td>
                          <strong>{order.orderNumber || order.id}</strong>
                        </td>
                        <td>
                          {order.createdAt 
                            ? formatDate(new Date(order.createdAt.seconds * 1000))
                            : 'N/A'}
                        </td>
                        <td>
                          <span className={getOrderStatusClass(order.status)}>
                            {getOrderStatusLabel(order.status)}
                          </span>
                        </td>
                        <td>
                          <strong>{formatCurrency(order.total || 0)}</strong>
                        </td>
                        <td>
                          <Link to={`/admin/pedidos-compra/${order.id}`}>
                            <button className="action-btn" title="Ver detalle">
                              👁️
                            </button>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Tab: Estadísticas */}
        {activeTab === 'stats' && stats && (
          <div className="stats-tab">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">📦</div>
                <div className="stat-value">{stats.totalProducts}</div>
                <div className="stat-label">Productos</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🛒</div>
                <div className="stat-value">{stats.totalOrders}</div>
                <div className="stat-label">Pedidos de Compra</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">💰</div>
                <div className="stat-value">{formatCurrency(stats.totalSpent)}</div>
                <div className="stat-label">Total Comprado</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">📊</div>
                <div className="stat-value">{formatCurrency(stats.averageOrder)}</div>
                <div className="stat-label">Ticket Promedio</div>
              </div>
            </div>

            {stats.firstOrderDate && (
              <div className="stats-dates">
                <div className="date-item">
                  <span className="date-label">Primer Pedido:</span>
                  <span className="date-value">{formatDate(stats.firstOrderDate)}</span>
                </div>
                {stats.lastOrderDate && (
                  <div className="date-item">
                    <span className="date-label">Último Pedido:</span>
                    <span className="date-value">{formatDate(stats.lastOrderDate)}</span>
                  </div>
                )}
              </div>
            )}

            {stats.topProducts && stats.topProducts.length > 0 && (
              <div className="top-products-section">
                <h3>Productos Más Comprados</h3>
                <div className="top-products-list">
                  {stats.topProducts.map((item, index) => (
                    <div key={item.productId} className="top-product-item">
                      <span className="product-rank">#{index + 1}</span>
                      <span className="product-id">Producto: {item.productId}</span>
                      <span className="product-quantity">{item.quantity} unidades</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default SupplierDetail

